import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo, BRAND_NAME } from "../components/ui/Logo";
import { Button } from "../components/ui/button";
import { submitPublicContactForm } from "../lib/api";
import {
  Gift,
  ClipboardList,
  MessageCircle,
  UserCircle,
  Loader2,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const LATAM_COUNTRIES = [
  "Argentina",
  "Belice",
  "Bolivia",
  "Brasil",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Cuba",
  "Ecuador",
  "El Salvador",
  "Guatemala",
  "Honduras",
  "México",
  "Nicaragua",
  "Panamá",
  "Paraguay",
  "Perú",
  "Puerto Rico",
  "República Dominicana",
  "Uruguay",
  "Venezuela",
];

const BENEFITS = [
  "🎯 Acceso completo a todas las funciones",
  "👥 Pacientes ilimitados — sin restricciones",
  "🎨 Personalización de tu clínica — agrega tu logo, firma, nombre y 2 frases en todos tus informes",
  "📅 Agenda médica con recordatorios automáticos",
  "📄 Impresión y descarga ilimitada de informes PDF",
  "💾 Tus datos son 100% tuyos — descarga toda tu base de datos cuando quieras. Aurora no retiene ningún dato tuyo.",
];

const COMMITMENTS = [
  "Completar mínimo 3 evaluaciones reales",
  "Responder formulario de feedback de 5 preguntas",
  "Opcional: compartir tu experiencia como testimonial",
];

const initialForm = {
  full_name: "",
  specialty: "",
  country: "",
  email: "",
  phone: "",
  feedback_consent: false,
};

const inputClass =
  "w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-300 bg-white";

export default function Fundadores() {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.full_name.trim()) {
      setError("El nombre completo es obligatorio.");
      return;
    }
    if (!formData.specialty.trim()) {
      setError("La especialidad médica es obligatoria.");
      return;
    }
    if (!formData.country.trim()) {
      setError("Seleccione su país.");
      return;
    }
    if (!formData.email.trim()) {
      setError("El email es obligatorio.");
      return;
    }
    if (!formData.feedback_consent) {
      setError("Debe aceptar compartir su feedback con el equipo.");
      return;
    }

    const message = [
      "Programa: Médicos Fundadores Aurora",
      `Especialidad: ${formData.specialty.trim()}`,
      `País: ${formData.country.trim()}`,
      formData.phone.trim() ? `WhatsApp: ${formData.phone.trim()}` : null,
      "Acepta compartir feedback con el equipo: Sí",
    ]
      .filter(Boolean)
      .join("\n");

    setLoading(true);
    try {
      await submitPublicContactForm({
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        specialty: formData.specialty.trim(),
        country: formData.country.trim(),
        phone: formData.phone.trim(),
        message,
      });
      setSuccess(true);
      setFormData(initialForm);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error al enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Programa Médicos Fundadores – Aurora Geriátrica</title>
        <meta name="description"
          content="Solo 50 cupos. Únete como médico fundador: 3 meses gratis, pacientes ilimitados, +26 escalas clínicas validadas. Sin tarjeta de crédito." />
        <meta property="og:title"
          content="Programa Médicos Fundadores – Aurora Geriátrica" />
        <meta property="og:description"
          content="Solo 50 cupos. 3 meses gratis, pacientes ilimitados, +26 escalas clínicas. Sin tarjeta de crédito." />
        <meta property="og:image"
          content="https://aurorageriatrica.com/og-fundadores.jpg" />
        <meta property="og:url"
          content="https://aurorageriatrica.com/fundadores" />
        <meta property="og:type" content="website" />
      </Helmet>
    <div className="min-h-screen bg-gradient-to-b from-sky-50/80 via-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-slate-200/80 bg-white/90 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3.5 flex items-center justify-between gap-3">
          <Link to="/" className="flex min-w-0 flex-1 items-center justify-center sm:justify-start" aria-label={BRAND_NAME}>
            <Logo
              variant="wordmark"
              format="png"
              className="h-[1.65rem] w-auto max-h-8 max-w-[min(52vw,220px)] object-contain object-center sm:h-7 sm:object-left"
              alt={BRAND_NAME}
            />
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm" className="rounded-xl border-slate-300 text-xs sm:text-sm h-9">
              Iniciar sesión
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pb-14 pt-8 sm:pt-12 sm:pb-20">
        {/* Hero */}
        <section className="text-center mb-10 sm:mb-14">
          <div className="flex justify-center mb-5">
            <Logo
              variant="wordmark"
              format="png"
              className="h-10 sm:h-12 w-auto max-w-[min(85vw,300px)] object-contain"
              alt={BRAND_NAME}
            />
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3.5 py-1.5 text-xs font-semibold text-amber-900 shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-amber-600" aria-hidden />
            Solo 50 cupos disponibles
          </span>
          <h1 className="mt-5 text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
            Programa Médicos Fundadores Aurora
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto">
            Sé parte del equipo que está construyendo la plataforma geriátrica más completa de Latinoamérica
          </p>
        </section>

        {/* Lo que recibes */}
        <section className="mb-8 sm:mb-10 rounded-2xl border border-sky-200 bg-white p-5 sm:p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Gift className="h-5 w-5 text-sky-600 shrink-0" aria-hidden />
            Lo que recibes
          </h2>
          <ul className="mt-4 space-y-3">
            {BENEFITS.map((item) => (
              <li key={item} className="text-sm sm:text-base text-slate-700 leading-relaxed">
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Lo que pedimos */}
        <section className="mb-8 sm:mb-10 rounded-2xl border border-slate-200 bg-slate-50/80 p-5 sm:p-6">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-slate-600 shrink-0" aria-hidden />
            Lo que pedimos a cambio
          </h2>
          <ul className="mt-4 space-y-3">
            {COMMITMENTS.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Formulario */}
        <section
          id="inscripcion"
          className="mb-8 sm:mb-10 rounded-2xl border-2 border-sky-200 bg-white p-5 sm:p-7 shadow-md shadow-sky-100/50"
        >
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-sky-600 shrink-0" aria-hidden />
            Inscripción al programa
          </h2>
          <p className="mt-1.5 text-sm text-slate-600">
            Completa el formulario y te contactaremos con tu acceso fundador.
          </p>

          {success ? (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-8 text-center">
              <p className="text-base sm:text-lg font-semibold text-emerald-900 leading-snug">
                ¡Bienvenido al programa! Recibirás tu acceso en las próximas 24 horas en tu email.
              </p>
              <Link
                to="/"
                className="mt-5 inline-block text-sm font-medium text-sky-700 hover:text-sky-800 underline"
              >
                Volver al inicio
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="full_name" className="block text-xs font-medium text-slate-700 mb-1">
                  Nombre completo <span className="text-red-600">*</span>
                </label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  autoComplete="name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="specialty" className="block text-xs font-medium text-slate-700 mb-1">
                  Especialidad médica <span className="text-red-600">*</span>
                </label>
                <input
                  id="specialty"
                  name="specialty"
                  type="text"
                  required
                  placeholder="Geriatría, Medicina interna..."
                  value={formData.specialty}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="country" className="block text-xs font-medium text-slate-700 mb-1">
                  País <span className="text-red-600">*</span>
                </label>
                <select
                  id="country"
                  name="country"
                  required
                  value={formData.country}
                  onChange={handleChange}
                  className={`${inputClass} appearance-none`}
                >
                  <option value="">Seleccione su país</option>
                  {LATAM_COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-medium text-slate-700 mb-1">
                  Email <span className="text-red-600">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs font-medium text-slate-700 mb-1">
                  WhatsApp <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+502 0000 0000"
                  value={formData.phone}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-100 bg-slate-50/80 p-3.5">
                <input
                  type="checkbox"
                  name="feedback_consent"
                  checked={formData.feedback_consent}
                  onChange={handleChange}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                <span className="text-sm text-slate-700 leading-snug">
                  Acepto compartir mi feedback con el equipo
                </span>
              </label>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" role="alert">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-sky-600 hover:bg-sky-700 text-base font-semibold shadow-sm shadow-sky-600/25 gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                    Enviando...
                  </>
                ) : (
                  <>
                    Quiero ser Médico Fundador
                    <ArrowRight className="h-5 w-5" aria-hidden />
                  </>
                )}
              </Button>
            </form>
          )}
        </section>

        {/* Quiénes somos */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <UserCircle className="h-5 w-5 text-violet-600 shrink-0" aria-hidden />
            Quiénes somos
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-700 leading-relaxed">
            Aurora Geriátrica fue construida desde adentro — por una médica que la usa en su práctica clínica
            diaria. Cada escala, cada flujo y cada informe fue diseñado pensando en lo que el médico realmente
            necesita en consulta.
          </p>
        </section>

        <footer className="mt-12 text-center text-xs text-slate-500 space-y-2">
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
            <Link to="/" className="hover:text-sky-600">
              Inicio
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/precios" className="hover:text-sky-600">
              Precios
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/contacto" className="hover:text-sky-600">
              Contacto
            </Link>
          </div>
          <p>© 2026 Aurora Geriátrica, S.A.</p>
        </footer>
      </main>
    </div>
    </>
  );
}
